import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

interface MediaMessageProps {
  url: string;
  type: 'image' | 'video' | 'gif';
  filename: string;
  size: number;
}

export default function MediaMessage({ url, type, filename, size }: MediaMessageProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleSave = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${type === 'image' ? 'Image' : type === 'gif' ? 'GIF' : 'Video'} saved successfully!`);
    } catch (error) {
      console.error('Error saving media:', error);
      toast.error('Failed to save media');
    }
  };

  const renderMedia = () => {
    if (type === 'gif') {
      return (
        <div className="relative group max-w-sm">
          <img
            src={url}
            alt="GIF"
            className="rounded-lg max-w-full h-auto shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setIsFullscreen(true)}
            style={{ maxHeight: '300px' }}
          />
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      );
    }
    
    if (type === 'image') {
      return (
        <div className="relative group max-w-xs">
          <img
            src={url}
            alt={filename}
            className="max-w-xs max-h-80 object-cover cursor-pointer rounded-lg"
            onClick={() => setIsFullscreen(true)}
          />
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (type === 'video') {
      return (
        <div className="relative group">
          <video
            src={url}
            controls
            controlsList="nodownload noplaybackrate"
            preload="metadata"
            muted
            playsInline
            width="320"
            height="240"
            className="max-w-xs max-h-80 object-cover rounded-lg bg-black"
            style={{ minHeight: '120px' }}
            onLoadStart={() => console.log('Video loading started:', url)}
            onLoadedMetadata={() => console.log('Video metadata loaded:', url)}
            onError={(e) => {
              console.error('Video failed to load:', url, e);
              const target = e.currentTarget as HTMLVideoElement;
              console.error('Video error details:', {
                error: target.error,
                networkState: target.networkState,
                readyState: target.readyState
              });
            }}
          >
            <source src={url} type="video/mp4" />
            <source src={url} type="video/webm" />
            Your browser does not support the video tag.
          </video>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="relative">
      {renderMedia()}
      
      {/* Fullscreen modal */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <div className="max-w-full max-h-full relative group">
            {type === 'gif' || type === 'image' ? (
              <img
                src={url}
                alt={filename}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <video
                src={url}
                controls
                controlsList="nodownload noplaybackrate"
                preload="metadata"
                muted={false}
                playsInline
                className="max-w-full max-h-full object-contain bg-black"
                onLoadStart={() => console.log('Fullscreen video loading started:', url)}
                onLoadedMetadata={() => console.log('Fullscreen video metadata loaded:', url)}
                onError={(e) => {
                  console.error('Fullscreen video failed to load:', url, e);
                }}
              >
                <source src={url} type="video/mp4" />
                <source src={url} type="video/webm" />
                Your browser does not support the video tag.
              </video>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              size="sm"
              variant="secondary"
              className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10 p-0"
            >
              <Download className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}